/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import PullRequestCommentInput from './PullRequestCommentInput';
import {DiffSide} from './generated/graphql';
import {
  gitHubClientAtom,
  gitHubPullRequestAtom,
  gitHubPullRequestNewCommentInputCellAtom,
} from './jotai';
import useRefreshPullRequest from './useRefreshPullRequest';
import {Box, Text} from '@primer/react';
import {useAtomValue, useSetAtom} from 'jotai';
import {useCallback} from 'react';

type Props = {
  line: number;
  path: string;
  side: DiffSide;
  startLine?: number;
  startSide?: DiffSide;
};

export default function PullRequestNewCommentInput({line, path, side, startLine, startSide}: Props): React.ReactElement {
  const setCellAtom = useSetAtom(gitHubPullRequestNewCommentInputCellAtom);
  const onCancel = useCallback(() => setCellAtom(null), [setCellAtom]);
  const refreshPullRequest = useRefreshPullRequest();

  // Client is already loaded by the time we're adding a comment
  const client = useAtomValue(gitHubClientAtom);

  // Read pull request ID from Jotai
  const pullRequest = useAtomValue(gitHubPullRequestAtom);

  const isMultiLine = startLine != null && startLine !== line;

  const addComment = useCallback(
    async (comment: string): Promise<void> => {
      if (client == null) {
        return Promise.reject('client not found');
      }

      const pullRequestId = pullRequest?.id;
      if (pullRequestId == null) {
        return Promise.reject('pull request id not found');
      }

      await client.addPullRequestReviewThread({
        body: comment,
        line,
        side,
        path,
        pullRequestId,
        ...(isMultiLine ? {startLine, startSide: startSide ?? side} : {}),
      });

      onCancel();
      refreshPullRequest();
    },
    [client, isMultiLine, line, onCancel, path, pullRequest, refreshPullRequest, side, startLine, startSide],
  );

  const lineLabel = isMultiLine ? `lines ${startLine}–${line}` : `line ${line}`;

  return (
    <Box backgroundColor="canvas.subtle" fontFamily="normal" padding={2} sx={{position: 'relative', zIndex: 1}}>
      <Box borderColor="border.default" borderWidth={1} borderStyle="solid">
        <Box padding={2}>
          <Text>
            Commenting on <Text fontWeight="bold">{lineLabel}</Text>
          </Text>
        </Box>
        {/* Do not reset input after adding a comment because addComment unmounts it. */}
        <PullRequestCommentInput
          addComment={addComment}
          onCancel={onCancel}
          autoFocus={true}
          resetInputAfterAddingComment={false}
        />
      </Box>
    </Box>
  );
}
